import React, { useState, useEffect, useRef } from "react";
import {
    InputBase,
    Paper,
    List,
    ListItem,
    ListItemText,
    Divider,
    IconButton,
    InputAdornment,
    CircularProgress,
    Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { formatTaskTitle } from "../../components/task/functions";

interface TaskSearchBarProps {
    handleOpenEditTaskModal: (task: any) => void;
}

const TaskSearchBar: React.FC<TaskSearchBarProps> = ({ handleOpenEditTaskModal }) => {
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const history = useHistory();

    const searchBarRef = useRef<HTMLDivElement>(null);
    const [dropdownWidth, setDropdownWidth] = useState<number | undefined>(undefined);

    // Track the latest request
    const latestRequest = useRef<number | null>(null);

    // Update dropdown width on render or query change
    useEffect(() => {
        if (searchBarRef.current) {
            setDropdownWidth(searchBarRef.current.offsetWidth);
        }
    }, [query]);

    // Search effect with debounce and abort
    useEffect(() => {
        if (!query) {
            setSuggestions([]);
            setTotalCount(null);
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        const requestId = Math.random(); // unique id for this request
        latestRequest.current = requestId;

        setLoading(true);

        const timeout = setTimeout(async () => {
            try {
                const res = await axios.get(`/tasks-search`, {
                    params: { query, suggestion: true, limit: 5 },
                    signal: controller.signal,
                });

                // Only update state if this request is the latest
                if (latestRequest.current === requestId) {
                    setSuggestions(res.data.data || []);
                    setTotalCount(res.data.meta?.total ?? null);
                    setLoading(false);
                }
            } catch (err: any) {
                if (err.name === "CanceledError") return; // Ignore aborted request
                console.error("Search failed", err);
                if (latestRequest.current === requestId) setLoading(false);
            }
        }, 300);

        return () => {
            clearTimeout(timeout); // cancel debounce timeout
            controller.abort(); // cancel previous API request
        };
    }, [query]);

    const handleSelectTask = (task: any) => {
        handleOpenEditTaskModal(task);
        setQuery("");
        setSuggestions([]);
        setTotalCount(null);
    };

    const handleShowAll = () => {
        history.push(`/tasks/search?query=${encodeURIComponent(query)}`);
        setSuggestions([]);
        setTotalCount(null);
    };

    const handleClear = () => {
        setQuery("");
        setSuggestions([]);
        setTotalCount(null);
    };

    return (
        <div style={{ position: "relative", flex: 1 }}>
            <Paper
                ref={searchBarRef}
                sx={{
                    p: "2px 8px",
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 2,
                    flexGrow: 1,
                }}
            >
                <SearchIcon sx={{ color: "gray", mr: 1 }} />
                <InputBase
                    placeholder="Search tasks..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    sx={{ flex: 1 }}
                    endAdornment={
                        query && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={handleClear}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        )
                    }
                />
            </Paper>

            {(loading || suggestions.length > 0 || totalCount !== null) && (
                <Paper
                    sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        width: dropdownWidth || "100%",
                        mt: 1,
                        zIndex: 9999,
                        maxHeight: 300,
                        overflowY: "auto",
                    }}
                >
                    <List>
                        {loading ? (
                            <ListItem>
                                <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                                    <CircularProgress size={20} />
                                    <ListItemText primary="Searching..." sx={{ ml: 1 }} />
                                </Box>
                            </ListItem>
                        ) : suggestions.length > 0 ? (
                            <>
                                {suggestions.map((task) => (
                                    <ListItem button key={task.id} onClick={() => handleSelectTask(task)}>
                                        <ListItemText
                                            primary={formatTaskTitle(task)}
                                            secondary={task.project?.name}
                                        />
                                    </ListItem>
                                ))}

                                {totalCount && totalCount > suggestions.length && (
                                    <>
                                        <Divider />
                                        <ListItem button onClick={handleShowAll}>
                                            <ListItemText primary={`Show all results (${totalCount})`} />
                                        </ListItem>
                                    </>
                                )}
                            </>
                        ) : (
                            <ListItem>
                                <ListItemText primary="No results found" />
                            </ListItem>
                        )}
                    </List>
                </Paper>
            )}
        </div>
    );
};

export default TaskSearchBar;
